import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';

// Backend SQL BIT columns come through the JArray serializer as the
// strings "True"/"False", not JS booleans. Boolean("False") is truthy,
// so use this parser instead of a bare Boolean(...) coercion.
const parseBool = (value: unknown): boolean => {
	if (typeof value === 'boolean') return value;
	if (typeof value === 'number') return value !== 0;
	if (typeof value === 'string') return value.toLowerCase() === 'true';
	return false;
};

// Main transformation function
export function apiRawToParkingLocation(rawData: any): ParkingLocation {
	const {
		parkingLocationId,
		name,
		companyId,
		violationCheckType,
		violationCheckWindowDays,
		maxStayMinutes,
		taggingPolicyType,
		taggingAfterViolations,
		towPolicyType,
		towAfterViolations,
		showConfirmStage,
		showScanPreview,
		showActionStage,
		showCarPhotoStage,
		enableOverstayNotifications,
		towingCompanyNumber,
		towingCompanyName,
		towInstructions,
		parkingZoneOptions,
	} = rawData;

	if (
		violationCheckType !== 'rolling' &&
		violationCheckType !== 'fixedMonthly' &&
		violationCheckType !== 'hourlyExpiry'
	) {
		throw new Error(`Invalid violationCheckType: ${violationCheckType}`);
	}
	if (
		taggingPolicyType !== 'never' &&
		taggingPolicyType !== 'afterXViolations'
	) {
		throw new Error(`Invalid taggingPolicyType: ${taggingPolicyType}`);
	}
	if (towPolicyType !== 'never' && towPolicyType !== 'afterXViolations') {
		throw new Error(`Invalid towPolicyType: ${towPolicyType}`);
	}
	if (!Array.isArray(parkingZoneOptions)) {
		throw new Error(`Invalid parkingZoneOptions: ${parkingZoneOptions}`);
	}

	return {
		id: String(parkingLocationId),
		name: name ?? '',
		customerID: String(companyId),

		// Rules
		violationCheckType,
		violationCheckWindowDays: violationCheckWindowDays
			? Number(violationCheckWindowDays)
			: undefined,
		maxStayMinutes:
			maxStayMinutes !== undefined && maxStayMinutes !== null
				? Number(maxStayMinutes)
				: undefined,
		taggingPolicyType:
			taggingPolicyType === 'never' ? 'never' : 'afterXViolations',
		taggingAfterViolations: taggingAfterViolations
			? Number(taggingAfterViolations)
			: undefined,
		towPolicyType: towPolicyType === 'never' ? 'never' : 'afterXViolations',
		towAfterViolations: towAfterViolations
			? Number(towAfterViolations)
			: undefined,

		// Stage gates (positive naming: true = stage runs)
		showScanPreview: parseBool(showScanPreview),
		showActionStage: parseBool(showActionStage),
		showCarPhotoStage: parseBool(showCarPhotoStage),
		showConfirmStage: parseBool(showConfirmStage),

		// Notifications
		enableOverstayNotifications: parseBool(enableOverstayNotifications),

		// Towing
		towingCompanyNumber: towingCompanyNumber ?? '',
		towingCompanyName: towingCompanyName ?? '',
		towInstructions: towInstructions ?? '',

		// Zones
		parkingZoneOptions: Array.isArray(parkingZoneOptions)
			? parkingZoneOptions.map((zoneOption) => ({
					id: Number(zoneOption.parkingZoneOptionId),
					name: zoneOption.name ?? '',
			  }))
			: [],
	};
}
